import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Search, FileText, ChevronDown, X } from 'lucide-react'
import type { WhatsAppTemplate } from '@/components/inbox/types'
import WhatsAppBadge from './WhatsAppBadge'

interface WhatsAppTemplateSelectorProps {
  templates: WhatsAppTemplate[]
  onSend: (templateId: number, variables: string[]) => Promise<boolean> | boolean
  onCancel?: () => void
  isLoading?: boolean
  sendDisabled?: boolean
  className?: string
  showWindowExpiredNotice?: boolean
}

export default function WhatsAppTemplateSelector({
  templates,
  onSend,
  onCancel,
  isLoading = false,
  sendDisabled = false,
  className = '',
  showWindowExpiredNotice = false,
}: WhatsAppTemplateSelectorProps) {
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)
  const [templateVariables, setTemplateVariables] = useState<Record<number, string>>({})
  const [templateSearchQuery, setTemplateSearchQuery] = useState('')
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false)
  const [templateHoverIndex, setTemplateHoverIndex] = useState(0)
  const [isSending, setIsSending] = useState(false)
  const templateSearchInputRef = useRef<HTMLInputElement>(null)

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) ?? null,
    [templates, selectedTemplateId],
  )

  const filteredTemplates = useMemo(
    () => {
      if (!templateSearchQuery.trim()) return templates
      const query = templateSearchQuery.toLowerCase()
      return templates.filter(
        (template) =>
          template.name.toLowerCase().includes(query) ||
          template.body_text.toLowerCase().includes(query),
      )
    },
    [templates, templateSearchQuery],
  )

  const hoveredTemplate = filteredTemplates[templateHoverIndex] ?? null

  const templatePreview = useMemo(() => {
    if (!selectedTemplate) return ''
    let text = selectedTemplate.body_text
    selectedTemplate.variables.forEach((variable) => {
      const value = templateVariables[variable.index]
      text = text.replace(`{{${variable.index}}}`, value || `{{${variable.index}}}`)
    })
    return text
  }, [selectedTemplate, templateVariables])

  const orderedVariables = useMemo(
    () =>
      selectedTemplate?.variables.map((variable) => templateVariables[variable.index] ?? '') ?? [],
    [selectedTemplate, templateVariables],
  )

  const allVariablesFilled = useMemo(
    () => orderedVariables.every((value) => value.trim().length > 0),
    [orderedVariables],
  )

  const isBusy = isLoading || isSending
  const canSend = !!selectedTemplate && allVariablesFilled && !sendDisabled && !isBusy

  useEffect(() => {
    setTemplateHoverIndex(0)
  }, [templateSearchQuery])

  useEffect(() => {
    if (templatePickerOpen) {
      templateSearchInputRef.current?.focus()
    }
  }, [templatePickerOpen])

  useEffect(() => {
    setTemplateVariables({})
  }, [selectedTemplateId])

  const selectTemplate = useCallback((template: WhatsAppTemplate) => {
    setSelectedTemplateId(template.id)
    setTemplatePickerOpen(false)
    setTemplateSearchQuery('')
  }, [])

  const handleSend = useCallback(async () => {
    if (!selectedTemplate || !allVariablesFilled || sendDisabled || isBusy) return
    setIsSending(true)
    try {
      const success = await onSend(selectedTemplate.id, orderedVariables)
      if (success) {
        setSelectedTemplateId(null)
        setTemplateVariables({})
        setTemplateSearchQuery('')
        setTemplatePickerOpen(false)
      }
    } catch {
      // Parent handles error feedback.
    } finally {
      setIsSending(false)
    }
  }, [allVariablesFilled, isBusy, onSend, orderedVariables, selectedTemplate, sendDisabled])

  return (
    <div className={`space-y-4 ${className}`}>
      {showWindowExpiredNotice && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2">
            <WhatsAppBadge size="sm" className="text-green-600" />
            <span className="text-sm font-medium text-amber-800">Janela de 24h expirada</span>
          </div>
          <p className="text-xs text-amber-700">
            Use um template pré-aprovado para retomar a conversa. O cliente pode responder e reabrir a janela de 24h.
          </p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Template</label>
        <button
          type="button"
          onClick={() => setTemplatePickerOpen(true)}
          className={`w-full text-left text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent flex items-center gap-2 relative ${
            selectedTemplate ? 'text-gray-900' : 'text-gray-500'
          }`}
        >
          <FileText className="w-4 h-4 text-gray-400 shrink-0" />
          <span className="truncate">{selectedTemplate?.name ?? 'Selecione um template'}</span>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </button>
      </div>

      {templatePickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setTemplatePickerOpen(false)}
          />
          <div
            className="relative bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden w-[640px] max-w-[90vw]"
            style={{ height: '440px' }}
          >
            <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3">
              <h3 className="text-sm font-semibold text-gray-900 shrink-0">Selecionar template</h3>
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  ref={templateSearchInputRef}
                  type="text"
                  value={templateSearchQuery}
                  onChange={(event) => setTemplateSearchQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (filteredTemplates.length === 0) return
                    switch (event.key) {
                      case 'ArrowDown':
                        event.preventDefault()
                        setTemplateHoverIndex((index) => Math.min(index + 1, filteredTemplates.length - 1))
                        break
                      case 'ArrowUp':
                        event.preventDefault()
                        setTemplateHoverIndex((index) => Math.max(index - 1, 0))
                        break
                      case 'Enter':
                        event.preventDefault()
                        if (hoveredTemplate) selectTemplate(hoveredTemplate)
                        break
                      case 'Escape':
                        event.preventDefault()
                        setTemplatePickerOpen(false)
                        break
                    }
                  }}
                  placeholder="Buscar template..."
                  className="w-full text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <button
                type="button"
                onClick={() => setTemplatePickerOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {filteredTemplates.length === 0 ? (
              <div className="flex-1 flex items-center justify-center p-4">
                <p className="text-sm text-gray-500">Nenhum template encontrado</p>
              </div>
            ) : (
              <div className="flex flex-1 min-h-0">
                <div className="w-1/2 h-full border-r border-gray-200 overflow-y-auto">
                  {filteredTemplates.map((template, index) => (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => selectTemplate(template)}
                      onMouseEnter={() => setTemplateHoverIndex(index)}
                      className={`w-full text-left px-4 py-3 transition-colors cursor-pointer ${
                        index === templateHoverIndex ? 'bg-gray-100' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="text-sm font-medium text-gray-900 truncate">{template.name}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{template.language} · {template.category}</div>
                    </button>
                  ))}
                </div>

                <div className="w-1/2 h-full p-5 overflow-y-auto bg-gray-50/50">
                  {hoveredTemplate ? (
                    <>
                      <h3 className="font-semibold text-gray-900 text-sm">{hoveredTemplate.name}</h3>
                      <span className="inline-block mt-1 text-xs text-gray-500">{hoveredTemplate.language} · {hoveredTemplate.category}</span>
                      <div className="mt-3 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5">
                        <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{hoveredTemplate.body_text}</p>
                      </div>
                      {hoveredTemplate.variables.length > 0 && (
                        <p className="mt-2 text-xs text-gray-500">
                          {hoveredTemplate.variables.length} {hoveredTemplate.variables.length === 1 ? 'variável' : 'variáveis'}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">Selecione um template para ver o conteúdo</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {selectedTemplate && selectedTemplate.variables.length > 0 && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Variáveis</label>
          {selectedTemplate.variables.map((variable) => {
            let componentLabel: string
            switch (variable.component) {
              case 'header': componentLabel = 'Cabeçalho'; break
              case 'button': componentLabel = 'Botão'; break
              default: componentLabel = 'Corpo'
            }
            return (
              <div key={variable.index} className="flex items-center gap-2">
                <span className="text-xs text-gray-500 font-mono w-10 shrink-0">{`{{${variable.index}}}`}</span>
                <input
                  type="text"
                  value={templateVariables[variable.index] ?? ''}
                  onChange={(event) =>
                    setTemplateVariables((previous) => ({ ...previous, [variable.index]: event.target.value }))
                  }
                  placeholder={`${componentLabel} - variável {{${variable.index}}}`}
                  className="flex-1 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            )
          })}
        </div>
      )}

      {selectedTemplate && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Pré-visualização</label>
          <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3">
            <p className="text-sm text-gray-800 whitespace-pre-wrap">{templatePreview}</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
          >
            Cancelar
          </button>
        )}
        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          className="px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Enviar template
        </button>
      </div>
    </div>
  )
}
